import { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  "translation", "sentence_building", "listen_select", "image_sentence",
  "guided_writing", "short_quiz", "pronunciation", "shadowing", "reading_repetition",
];

export default function ExerciseBuilder({ lessonId, courseId, language = "en", onCreated }) {
  const { t } = useI18n();
  const [type, setType] = useState("translation");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const [f, setF] = useState({});
  const [options, setOptions] = useState("");          // listen_select options (one per line)
  const [correctIndex, setCorrectIndex] = useState(0); // listen_select
  const [questions, setQuestions] = useState([{ question: "", options: "", correct_index: 0 }]);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const buildConfig = () => {
    switch (type) {
      case "translation":
        return { source_text: f.source_text || "", source_lang: f.source_lang || "es", target_lang: f.target_lang || "en" };
      case "sentence_building": {
        const words = (f.words || "").split("\n").map((w) => w.trim()).filter(Boolean);
        const sentence = f.sentence || "";
        return { sentence, words: words.length ? words : sentence.split(/\s+/).filter(Boolean) };
      }
      case "pronunciation":
        return { target_phrase: f.target_phrase || "", language };
      case "shadowing":
      case "reading_repetition":
        return { text: f.text || "", language };
      case "listen_select": {
        const opts = options.split("\n").map((o) => o.trim()).filter(Boolean);
        return { phrase: f.phrase || "", options: opts, correct_index: Number(correctIndex), language };
      }
      case "image_sentence":
        return { image_url: f.image_url || "", hint: f.hint || "", language };
      case "guided_writing":
        return { guidance: f.guidance || "", language };
      case "short_quiz":
        return {
          questions: questions.map((q) => ({
            question: q.question,
            options: q.options.split("\n").map((o) => o.trim()).filter(Boolean),
            correct_index: Number(q.correct_index),
          })),
        };
      default:
        return {};
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/exercises", { lesson_id: lessonId, course_id: courseId, type, prompt, config: buildConfig() });
      toast.success(t("teacher.add_exercise"));
      setPrompt(""); setF({}); setOptions(""); setCorrectIndex(0);
      setQuestions([{ question: "", options: "", correct_index: 0 }]);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const updateQuestion = (i, k, v) => setQuestions((qs) => qs.map((q, idx) => idx === i ? { ...q, [k]: v } : q));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("teacher.exercise_type")}</Label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="rounded-xl" data-testid="exercise-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {TYPES.map((ty) => <SelectItem key={ty} value={ty}>{t(`ex.${ty}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{t("common.title")} / Prompt</Label>
        <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} className="rounded-xl" data-testid="exercise-prompt-input" />
      </div>

      {type === "translation" && (
        <>
          <div className="space-y-2"><Label>Source text</Label>
            <Input value={f.source_text || ""} onChange={(e) => set("source_text", e.target.value)} className="rounded-xl" data-testid="cfg-source-text" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>From</Label>
              <Select value={f.source_lang || "es"} onValueChange={(v) => set("source_lang", v)}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="es">ES</SelectItem><SelectItem value="en">EN</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>To</Label>
              <Select value={f.target_lang || "en"} onValueChange={(v) => set("target_lang", v)}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="en">EN</SelectItem><SelectItem value="es">ES</SelectItem></SelectContent></Select></div>
          </div>
        </>
      )}

      {type === "sentence_building" && (
        <>
          <div className="space-y-2"><Label>Target sentence</Label>
            <Input value={f.sentence || ""} onChange={(e) => set("sentence", e.target.value)} className="rounded-xl" data-testid="cfg-sentence" placeholder="How are you?" /></div>
          <div className="space-y-2"><Label>Word bank (one per line — optional)</Label>
            <Textarea value={f.words || ""} onChange={(e) => set("words", e.target.value)} className="rounded-xl" data-testid="cfg-words" placeholder="are&#10;you&#10;How" /></div>
        </>
      )}

      {type === "pronunciation" && (
        <div className="space-y-2"><Label>Target phrase</Label>
          <Input value={f.target_phrase || ""} onChange={(e) => set("target_phrase", e.target.value)} className="rounded-xl" data-testid="cfg-target-phrase" /></div>
      )}

      {(type === "shadowing" || type === "reading_repetition") && (
        <div className="space-y-2"><Label>Text</Label>
          <Textarea value={f.text || ""} onChange={(e) => set("text", e.target.value)} className="rounded-xl" data-testid="cfg-text" /></div>
      )}

      {type === "listen_select" && (
        <>
          <div className="space-y-2"><Label>Phrase to play (audio)</Label>
            <Input value={f.phrase || ""} onChange={(e) => set("phrase", e.target.value)} className="rounded-xl" data-testid="cfg-phrase" /></div>
          <div className="space-y-2"><Label>Options (one per line)</Label>
            <Textarea value={options} onChange={(e) => setOptions(e.target.value)} className="rounded-xl" data-testid="cfg-options" /></div>
          <div className="space-y-2"><Label>Correct option index (0-based)</Label>
            <Input type="number" min="0" value={correctIndex} onChange={(e) => setCorrectIndex(e.target.value)} className="rounded-xl w-24" data-testid="cfg-correct-index" /></div>
        </>
      )}

      {type === "image_sentence" && (
        <>
          <div className="space-y-2"><Label>Image URL</Label>
            <Input value={f.image_url || ""} onChange={(e) => set("image_url", e.target.value)} className="rounded-xl" data-testid="cfg-image-url" placeholder="https://..." /></div>
          <div className="space-y-2"><Label>Hint</Label>
            <Input value={f.hint || ""} onChange={(e) => set("hint", e.target.value)} className="rounded-xl" data-testid="cfg-hint" /></div>
        </>
      )}

      {type === "guided_writing" && (
        <div className="space-y-2"><Label>Guidance</Label>
          <Textarea value={f.guidance || ""} onChange={(e) => set("guidance", e.target.value)} className="rounded-xl" data-testid="cfg-guidance" /></div>
      )}

      {type === "short_quiz" && (
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={i} className="rounded-xl border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label>Q{i + 1}</Label>
                {questions.length > 1 && (
                  <button type="button" onClick={() => setQuestions((qs) => qs.filter((_, idx) => idx !== i))}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </button>
                )}
              </div>
              <Input value={q.question} onChange={(e) => updateQuestion(i, "question", e.target.value)} placeholder="Question" className="rounded-xl" data-testid={`quiz-q-${i}`} />
              <Textarea value={q.options} onChange={(e) => updateQuestion(i, "options", e.target.value)} placeholder="Options (one per line)" className="rounded-xl" data-testid={`quiz-opts-${i}`} />
              <div className="flex items-center gap-2">
                <Label className="text-xs">Correct index</Label>
                <Input type="number" min="0" value={q.correct_index} onChange={(e) => updateQuestion(i, "correct_index", e.target.value)} className="rounded-xl w-20" data-testid={`quiz-correct-${i}`} />
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="rounded-full"
            onClick={() => setQuestions((qs) => [...qs, { question: "", options: "", correct_index: 0 }])}>
            <Plus className="h-4 w-4 mr-1" /> {t("common.add")}
          </Button>
        </div>
      )}

      <Button onClick={submit} disabled={loading} className="rounded-full w-full" data-testid="submit-exercise-button">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("teacher.add_exercise")}
      </Button>
    </div>
  );
}
